import { createFileRoute } from '@tanstack/react-router'
import { Database, Save, Loader2 } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useSystemConfigs, useUpdateSystemConfig, type ConfigItem } from '@/hooks'

export const Route = createFileRoute('/_authenticated/settings/config' as never)({
  component: SystemConfigPage,
})

function SystemConfigPage() {
  const { data: configs, isLoading, error } = useSystemConfigs()
  const updateConfig = useUpdateSystemConfig()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">加载配置失败: {error.message}</p>
      </div>
    )
  }

  // configs is already grouped by category from API
  const configGroups = configs ?? []

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-green-500">
          <Database className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">系统配置</h1>
          <p className="text-muted-foreground">管理系统参数和基础配置</p>
        </div>
      </div>

      <div className="space-y-6">
        {configGroups.map((group) => (
          <div key={group.category} className="rounded-lg border bg-card">
            <div className="border-b p-4">
              <h2 className="font-semibold">{group.name}</h2>
              <p className="text-sm text-muted-foreground">
                {group.category === 'currency' && '配置系统使用的货币和显示格式'}
                {group.category === 'notification' && '配置系统通知渠道和触发器'}
                {group.category === 'general' && '系统通用配置项'}
                {group.category === 'security' && '安全相关配置'}
              </p>
            </div>
            <div className="p-4 space-y-4">
              {group.configs.map((config) => (
                <ConfigItemEditor
                  key={config.key}
                  config={config}
                  onUpdate={(value) => {
                    updateConfig.mutate({ key: config.key, data: { value } })
                  }}
                  isUpdating={updateConfig.isPending}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ConfigItemEditorProps {
  config: ConfigItem
  onUpdate: (value: unknown) => void
  isUpdating: boolean
}

function ConfigItemEditor({ config, onUpdate, isUpdating }: ConfigItemEditorProps) {
  const [localValue, setLocalValue] = useState(config.value)
  const hasChanged = JSON.stringify(localValue) !== JSON.stringify(config.value)

  const handleSave = () => {
    onUpdate(localValue)
  }

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
      <div className="flex-1">
        <p className="text-sm font-medium">{config.definition.name}</p>
        <p className="text-xs text-muted-foreground">{config.definition.description}</p>
      </div>
      <div className="flex items-center gap-2">
        {config.definition.type === 'boolean' && (
          <div className="flex items-center gap-3">
            <Switch
              checked={localValue as boolean}
              onCheckedChange={(checked) => {
                setLocalValue(checked)
                onUpdate(checked)
              }}
              disabled={isUpdating}
            />
            <span className={`text-sm font-medium ${localValue ? 'text-green-600' : 'text-muted-foreground'}`}>
              {localValue ? '已开启' : '已关闭'}
            </span>
          </div>
        )}
        {config.definition.type === 'enum' && config.definition.options && (
          <Select
            value={localValue as string}
            onValueChange={(value: string) => {
              setLocalValue(value)
              onUpdate(value)
            }}
            disabled={isUpdating}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {config.definition.options.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {config.definition.type === 'number' && (
          <>
            <input
              type="number"
              value={localValue as number}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalValue(Number(e.target.value))}
              className="w-24 rounded-md border px-3 py-1.5 text-sm"
              min={config.definition.validation?.min}
              max={config.definition.validation?.max}
              disabled={isUpdating}
            />
            {hasChanged && (
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </button>
            )}
          </>
        )}
        {config.definition.type === 'string' && (
          <>
            <input
              type="text"
              value={localValue as string}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalValue(e.target.value)}
              className="w-48 rounded-md border px-3 py-1.5 text-sm"
              disabled={isUpdating}
            />
            {hasChanged && (
              <button
                onClick={handleSave}
                disabled={isUpdating}
                className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
